import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import './Admin.css';

// ── CONFIRM DIALOG ────────────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="admin-confirm-backdrop" onClick={onCancel}>
      <div className="admin-confirm-box" onClick={e => e.stopPropagation()}>
        <h3>Are you sure?</h3>
        <p>{message}</p>
        <div className="admin-confirm-actions">
          <button className="admin-btn admin-btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="admin-btn admin-btn-danger" onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, highlight }) {
  return (
    <div className={`admin-stat-card${highlight ? ' highlight' : ''}`}>
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">{value ?? '—'}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

// ── ROLE BADGE ────────────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const map = {
    transporter: { label: '✈️ Transporter', cls: 'role-transporter' },
    sender:      { label: '📦 Sender',      cls: 'role-sender' },
    both:        { label: '🔄 Both',         cls: 'role-both' },
  };
  const r = map[role] || { label: role || '—', cls: '' };
  return <span className={`admin-role-badge ${r.cls}`}>{r.label}</span>;
}

// ── STATUS BADGE ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    active:              'badge-green',
    full:                'badge-yellow',
    pending:             'badge-yellow',
    proposed:            'badge-yellow',
    confirmed:           'badge-green',
    collected:           'badge-green',
    departed:            'badge-green',
    in_transit:          'badge-yellow',
    delivered:           'badge-green',
    cancelled:           'badge-gray',
    rejected:            'badge-gray',
    completed:           'badge-green',
    could_not_deliver:   'badge-gray',
    lost:                'badge-gray',
    seized_by_customs:   'badge-gray',
    returned_to_sender:  'badge-gray',
  };
  return <span className={`badge ${map[status] || 'badge-gray'}`}>{status?.replace(/_/g, ' ') || '—'}</span>;
}

// ── PAYMENT BADGE ─────────────────────────────────────────────────────────────

function PaymentBadge({ paymentStatus, paymentMode }) {
  if (paymentStatus === 'paid')    return <span className="badge badge-green">💳 Paid online</span>;
  if (paymentStatus === 'cash')    return <span className="badge badge-yellow">💵 Cash</span>;
  if (paymentStatus === 'refunded') return <span className="badge badge-gray">↩️ Refunded</span>;
  if (paymentStatus === 'partially_refunded') return <span className="badge badge-yellow">↩️ Partial refund</span>;
  if (paymentStatus === 'unpaid')  return <span className="badge badge-gray">⏳ Unpaid</span>;
  return <span className="badge badge-gray">{paymentStatus || '—'}</span>;
}

// ── USERS SECTION ─────────────────────────────────────────────────────────────

function UsersSection({ users, onVerify, onUnverify }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = users.filter(u => {
    const matchSearch =
      u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.city?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filter === 'all' || u.role === filter;
    return matchSearch && matchRole;
  });

  return (
    <>
      <div className="admin-stats">
        <StatCard label="Total users"   value={users.length} />
        <StatCard label="Transporters"  value={users.filter(u => u.role === 'transporter' || u.role === 'both').length} />
        <StatCard label="Senders"       value={users.filter(u => u.role === 'sender' || u.role === 'both').length} />
        <StatCard label="Verified"      value={users.filter(u => u.is_verified).length} sub={`of ${users.length} total`} />
        <StatCard label="Pending KYC"   value={users.filter(u => u.verification_status === 'pending').length} highlight={users.filter(u => u.verification_status === 'pending').length > 0} />
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h3>All Users</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 10px', color: 'var(--sand)', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              <option value="all">All roles</option>
              <option value="transporter">Transporters</option>
              <option value="sender">Senders</option>
              <option value="both">Both</option>
            </select>
            <input className="admin-search" placeholder="Search by name or city…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>City</th>
              <th>Phone</th>
              <th>Joined</th>
              <th>Verification</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="admin-table-empty">No users found</td></tr>
            )}
            {filtered.map(u => (
              <tr key={u.id}>
                <td>
                  <div style={{ fontWeight: '600', color: 'var(--sand)' }}>{u.full_name || '—'}</div>
                  <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '2px' }}>{u.id.slice(0, 8)}…</div>
                </td>
                <td><RoleBadge role={u.role} /></td>
                <td>{u.city || '—'}</td>
                <td style={{ fontSize: '12px', color: 'var(--olive)' }}>{u.phone || '—'}</td>
                <td>{u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '—'}</td>
                <td>
                  {u.is_verified
                    ? <span className="badge badge-green">✓ Verified</span>
                    : u.verification_status === 'pending'
                      ? <span className="badge badge-yellow">⏳ Pending</span>
                      : u.verification_status === 'rejected'
                        ? <span className="badge badge-gray">✗ Rejected</span>
                        : <span className="badge badge-gray">Unverified</span>}
                </td>
                <td>
                  <div className="admin-actions">
                    {(u.role === 'transporter' || u.role === 'both') && (
                      u.is_verified
                        ? <button className="admin-btn admin-btn-unverify" onClick={() => onUnverify(u.id)}>Unverify</button>
                        : <button className="admin-btn admin-btn-verify" onClick={() => onVerify(u.id)}>✓ Verify</button>
                    )}
                    <Link
                      to={u.role === 'transporter' || u.role === 'both' ? `/transporter/${u.id}` : `/sender/${u.id}`}
                      className="admin-btn admin-btn-ghost"
                      target="_blank"
                    >
                      View profile
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── TRIPS SECTION ─────────────────────────────────────────────────────────────

function TripsSection({ trips, onCancel, onDelete }) {
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = trips.filter(t => {
    const matchSearch = `${t.origin_city} ${t.destination_city} ${t.transporter?.full_name || t.profiles?.full_name || ''}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || t.status === filterStatus;
    return matchSearch && matchStatus;
  });

  function handleAction(type, trip) {
    setConfirm({
      type,
      tripId: trip.id,
      message: type === 'cancel'
        ? `Cancel trip from ${trip.origin_city} to ${trip.destination_city}? All pending bookings will also be cancelled.`
        : `Permanently delete trip from ${trip.origin_city} to ${trip.destination_city}? This cannot be undone.`,
    });
  }

  return (
    <>
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.type === 'cancel') onCancel(confirm.tripId);
            else onDelete(confirm.tripId);
            setConfirm(null);
          }}
        />
      )}

      <div className="admin-stats">
        <StatCard label="Total trips"    value={trips.length} />
        <StatCard label="Active"         value={trips.filter(t => t.status === 'active').length} />
        <StatCard label="Full"           value={trips.filter(t => t.status === 'full').length} />
        <StatCard label="Completed"      value={trips.filter(t => t.status === 'completed').length} />
        <StatCard label="Cancelled"      value={trips.filter(t => t.status === 'cancelled').length} />
        <StatCard label="🛑 Multi-stop"  value={trips.filter(t => t.is_multi_stop).length} />
        <StatCard label="📄 Accepts docs" value={trips.filter(t => t.accepts_documents).length} />
        <StatCard label="DE → TN"        value={trips.filter(t => t.origin_country === 'Germany').length} />
        <StatCard label="TN → DE"        value={trips.filter(t => t.origin_country === 'Tunisia').length} />
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h3>All Trips</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 10px', color: 'var(--sand)', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              {['all','active','full','completed','cancelled'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <input className="admin-search" placeholder="Search route or transporter…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Route</th>
              <th>Transporter</th>
              <th>Departure</th>
              <th>Capacity</th>
              <th>Pricing</th>
              <th>Features</th>
              <th>Payment</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="admin-table-empty">No trips found</td></tr>
            )}
            {filtered.map(t => {
              const pct = t.total_kg > 0 ? Math.round(((t.total_kg - t.available_kg) / t.total_kg) * 100) : 0;
              const transporterName = t.transporter?.full_name || t.profiles?.full_name || '—';
              return (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight: '600', color: 'var(--sand)' }}>
                      {t.origin_city} → {t.destination_city}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '2px' }}>
                      {t.origin_country === 'Germany' ? '🇩🇪' : '🇹🇳'} → {t.destination_country === 'Tunisia' ? '🇹🇳' : '🇩🇪'}
                    </div>
                  </td>
                  <td>{transporterName}</td>
                  <td>{format(new Date(t.departure_date), 'MMM d, yyyy')}</td>
                  <td>
                    <div>{t.available_kg} / {t.total_kg} kg</div>
                    <div style={{ fontSize: '11px', color: 'var(--olive)' }}>{pct}% booked</div>
                  </td>
                  <td>
                    <div>€{t.price_per_kg}/kg</div>
                    {t.accepts_documents && <div style={{ fontSize: '11px', color: '#4CAF7D' }}>📄 €{t.document_price}/doc</div>}
                  </td>
                  <td>
                    {t.is_multi_stop    && <div style={{ fontSize: '11px' }}>🛑 Multi-stop</div>}
                    {t.accepts_documents && <div style={{ fontSize: '11px' }}>📄 Docs</div>}
                  </td>
                  <td>
                    {t.payment_mode === 'cash'     && <span style={{ fontSize: '12px' }}>💵 Cash</span>}
                    {t.payment_mode === 'platform' && <span style={{ fontSize: '12px' }}>💳 Online</span>}
                    {t.payment_mode === 'either'   && <span style={{ fontSize: '12px' }}>💳/💵 Flexible</span>}
                  </td>
                  <td><StatusBadge status={t.status} /></td>
                  <td>
                    <div className="admin-actions">
                      <Link to={`/trip/${t.id}`} className="admin-btn admin-btn-ghost" target="_blank">View</Link>
                      {t.status === 'active' && (
                        <button className="admin-btn admin-btn-danger" onClick={() => handleAction('cancel', t)}>Cancel</button>
                      )}
                      <button className="admin-btn admin-btn-danger" onClick={() => handleAction('delete', t)}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── BOOKINGS SECTION ──────────────────────────────────────────────────────────

function BookingsSection({ bookings }) {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterType,    setFilterType]    = useState('all');

  const ALL_STATUSES = ['all','proposed','pending','confirmed','collected','departed','delivered','cancelled','rejected'];

  const filtered = bookings.filter(b => {
    const senderName = b.sender?.full_name || b.profiles?.full_name || '';
    const matchSearch = `${senderName} ${b.trips?.origin_city || ''} ${b.trips?.destination_city || ''}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus  = filterStatus === 'all' || b.status === filterStatus;
    const matchPayment = filterPayment === 'all' || b.payment_status === filterPayment || b.sender_payment_mode === filterPayment;
    const matchType = filterType === 'all' || (filterType === 'kg' ? (!b.booking_type || b.booking_type === 'kg') : b.booking_type === filterType);
    return matchSearch && matchStatus && matchPayment && matchType;
  });

  return (
    <>
      <div className="admin-stats">
        <StatCard label="Total bookings" value={bookings.length} />
        <StatCard label="Proposed"    value={bookings.filter(b => b.status === 'proposed').length} />
        <StatCard label="Confirmed"   value={bookings.filter(b => b.status === 'confirmed').length} />
        <StatCard label="In transit"  value={bookings.filter(b => ['collected','departed'].includes(b.status)).length} />
        <StatCard label="Delivered"   value={bookings.filter(b => b.status === 'delivered').length} />
        <StatCard label="Cancelled"   value={bookings.filter(b => ['cancelled','rejected'].includes(b.status)).length} />
        <StatCard label="📦 Kg bookings"  value={bookings.filter(b => !b.booking_type || b.booking_type === 'kg').length} />
        <StatCard label="📄 Doc bookings" value={bookings.filter(b => b.booking_type === 'document').length} />
        <StatCard label="💳 Online paid"  value={bookings.filter(b => b.payment_status === 'paid').length} />
        <StatCard label="💵 Cash"         value={bookings.filter(b => b.payment_status === 'cash').length} />
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h3>All Bookings</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <select
              value={filterPayment}
              onChange={e => setFilterPayment(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 10px', color: 'var(--sand)', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              <option value="all">All payments</option>
              <option value="paid">💳 Online paid</option>
              <option value="cash">💵 Cash</option>
              <option value="unpaid">⏳ Unpaid</option>
              <option value="refunded">↩️ Refunded</option>
            </select>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 10px', color: 'var(--sand)', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              <option value="all">All types</option>
              <option value="kg">📦 Kg bookings</option>
              <option value="document">📄 Document bookings</option>
            </select>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 10px', color: 'var(--sand)', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              {ALL_STATUSES.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <input className="admin-search" placeholder="Search sender or route…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Sender</th>
              <th>Route</th>
              <th>Type</th>
              <th>Contents</th>
              <th>Total</th>
              <th>Platform fee</th>
              <th>Payment</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="admin-table-empty">No bookings found</td></tr>
            )}
            {filtered.map(b => (
              <tr key={b.id}>
                <td>
                  <div style={{ fontWeight: '600', color: 'var(--sand)' }}>
                    {b.sender?.full_name || b.profiles?.full_name || '—'}
                  </div>
                  {b.description && (
                    <div style={{ fontSize: '11px', color: 'var(--olive)', marginTop: '2px' }}>{b.description}</div>
                  )}
                </td>
                <td>
                  <div>{b.trips?.origin_city || '—'} → {b.trips?.destination_city || '—'}</div>
                  {b.trips?.profiles?.full_name && (
                    <div style={{ fontSize: '11px', color: 'var(--olive)' }}>🚚 {b.trips.profiles.full_name}</div>
                  )}
                </td>
                <td>
                  {(!b.booking_type || b.booking_type === 'kg')
                    ? <span style={{ fontSize: '12px', background: 'rgba(100,150,255,0.12)', color: '#6496FF', borderRadius: '6px', padding: '2px 8px' }}>📦 Kg</span>
                    : <span style={{ fontSize: '12px', background: 'rgba(76,175,125,0.12)', color: '#4CAF7D', borderRadius: '6px', padding: '2px 8px' }}>📄 Doc</span>
                  }
                </td>
                <td>
                  {b.kg_booked > 0 && <div style={{ fontSize: '12px' }}>{b.kg_booked} kg</div>}
                  {b.document_count > 0 && (
                    <div style={{ fontSize: '11px', color: '#4CAF7D' }}>
                      {b.document_count} × {
                        b.document_type === 'official' ? 'Official doc' :
                        b.document_type === 'business' ? 'Business doc' :
                        b.document_type === 'letter'   ? 'Letter' :
                        b.document_type === 'small_parcel' ? 'Small parcel' : b.document_type
                      }
                    </div>
                  )}
                  {b.description && <div style={{ fontSize: '11px', color: 'var(--olive)' }}>{b.description.slice(0,30)}{b.description.length > 30 ? '…' : ''}</div>}
                </td>
                <td>€{parseFloat(b.total_price || 0).toFixed(2)}</td>
                <td style={{ color: '#e8775a', fontWeight: '600' }}>€{parseFloat(b.platform_fee || 0).toFixed(2)}</td>
                <td><PaymentBadge paymentStatus={b.payment_status} paymentMode={b.sender_payment_mode} /></td>
                <td><StatusBadge status={b.status} /></td>
                <td style={{ fontSize: '12px', color: 'var(--olive)' }}>{format(new Date(b.created_at), 'MMM d, yyyy')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── REVENUE SECTION ───────────────────────────────────────────────────────────

function RevenueSection({ bookings }) {
  // Platform fee is collected at the time of payment (not just on delivery)
  // Online: payment_status='paid' → fee already collected via Stripe
  // Cash:   payment_status='cash' → platform fee still collected online via Stripe (sender pays fee + cash to transporter)
  const paidOnline   = bookings.filter(b => b.payment_status === 'paid');
  const paidCash     = bookings.filter(b => b.payment_status === 'cash');
  const delivered    = bookings.filter(b => b.status === 'delivered');
  const inTransit    = bookings.filter(b => ['confirmed','collected','departed'].includes(b.status));

  const onlineFeesCollected = paidOnline.reduce((s, b) => s + (b.platform_fee || 0), 0);
  const cashFeesPending     = paidCash.reduce((s, b) => s + (b.platform_fee || 0), 0);
  const totalRevenue        = onlineFeesCollected + cashFeesPending;
  const deliveredRevenue    = delivered.reduce((s, b) => s + (b.platform_fee || 0), 0);
  const totalVolume         = [...paidOnline, ...paidCash].reduce((s, b) => s + (b.total_price || 0), 0);

  // Document booking breakdown
  const docBookings     = bookings.filter(b => b.booking_type === 'document');
  const kgBookings      = bookings.filter(b => !b.booking_type || b.booking_type === 'kg');
  const docVolume       = docBookings.filter(b => ['paid','cash'].includes(b.payment_status)).reduce((s,b) => s + (b.total_price || 0), 0);
  const docFees         = docBookings.filter(b => ['paid','cash'].includes(b.payment_status)).reduce((s,b) => s + (b.platform_fee || 0), 0);
  const kgVolume        = kgBookings.filter(b => ['paid','cash'].includes(b.payment_status)).reduce((s,b) => s + (b.total_price || 0), 0);

  // Stripe processing fees (blended estimate)
  // Standard EEA cards:    1.5% + €0.25  (most German-resident senders)
  // Premium EEA cards:     1.9% + €0.25  (some Visa/MC premium)
  // International cards:   3.25% + €0.25 (Tunisian cards, non-EEA)
  // Blended rate assumption: ~70% EEA standard, ~10% EEA premium, ~20% international
  // → blended %: 0.7×1.5 + 0.1×1.9 + 0.2×3.25 = 1.84% + €0.25 per transaction
  const STRIPE_RATE = 0.0184;
  const STRIPE_FIXED = 0.25;
  const paidBookings = [...paidOnline, ...paidCash];
  const estimatedStripeFees = paidBookings.reduce((s, b) => {
    const chargedAmount = b.payment_status === 'cash'
      ? (b.platform_fee || 0)   // cash booking: only platform fee charged online
      : (b.total_price || 0);   // online booking: full amount charged
    return s + (chargedAmount * STRIPE_RATE + STRIPE_FIXED);
  }, 0);
  const netRevenue = totalRevenue - estimatedStripeFees;

  const byMonth = {};
  [...paidOnline, ...paidCash].forEach(b => {
    const month = format(new Date(b.created_at), 'MMM yyyy');
    if (!byMonth[month]) byMonth[month] = { online: 0, cash: 0, stripeFee: 0 };
    const fee = b.platform_fee || 0;
    const chargedAmount = b.payment_status === 'cash' ? fee : (b.total_price || 0);
    const stripeFee = chargedAmount * 0.0184 + 0.25;
    if (b.payment_status === 'paid') byMonth[month].online += fee;
    else byMonth[month].cash += fee;
    byMonth[month].stripeFee += stripeFee;
  });

  return (
    <>
      <div className="admin-stats">
        <StatCard label="Gross platform revenue"    value={`€${totalRevenue.toFixed(2)}`}            sub="Total platform fees collected"  highlight />
        <StatCard label="~Stripe fees (est.)"        value={`-€${estimatedStripeFees.toFixed(2)}`}      sub="Blended ~1.84% + €0.25 (EEA + intl cards)" />
        <StatCard label="Net revenue (est.)"         value={`€${netRevenue.toFixed(2)}`}                sub="After estimated Stripe fees" highlight />
        <StatCard label="💳 Full online payments"    value={`€${onlineFeesCollected.toFixed(2)}`}       sub="Platform fees from fully online bookings" />
        <StatCard label="💵 Cash booking fees"       value={`€${cashFeesPending.toFixed(2)}`}           sub="Platform fees collected online (cash bookings)" />
        <StatCard label="Delivered GMV"             value={`€${deliveredRevenue.toFixed(2)}`}          sub="Fees from delivered bookings" />
        <StatCard label="Total GMV"                 value={`€${totalVolume.toFixed(2)}`}               sub="Gross merchandise value" />
        <StatCard label="Avg fee / booking"         value={paidOnline.length + paidCash.length ? `€${(totalRevenue / (paidOnline.length + paidCash.length)).toFixed(2)}` : '—'} sub="Before Stripe fees" />
        <StatCard label="Paid bookings"             value={paidOnline.length + paidCash.length}        sub={`${paidOnline.length} online · ${paidCash.length} cash`} />
        <StatCard label="In transit"                value={inTransit.length}                           sub="Confirmed but not yet delivered" />
        <StatCard label="📦 Kg booking volume"   value={`€${kgVolume.toFixed(2)}`}          sub="GMV from kg bookings" />
        <StatCard label="📄 Doc booking volume"  value={`€${docVolume.toFixed(2)}`}         sub="GMV from document bookings" />
        <StatCard label="📄 Doc fees collected"  value={`€${docFees.toFixed(2)}`}           sub="Platform fees from docs" />
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h3>Revenue by Month</h3>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>💳 Online fees</th>
              <th>💵 Cash booking fees</th>
              <th>📦 Kg bookings</th>
              <th>📄 Doc bookings</th>
              <th>Total fees</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(byMonth).length === 0 && (
              <tr><td colSpan={6} className="admin-table-empty">No revenue data yet</td></tr>
            )}
            {Object.entries(byMonth).reverse().map(([month, { online, cash, doc }]) => (
              <tr key={month}>
                <td style={{ color: 'var(--sand)', fontWeight: '600' }}>{month}</td>
                <td style={{ color: '#2ecc71',  fontWeight: '700' }}>€{online.toFixed(2)}</td>
                <td style={{ color: '#F5A623',  fontWeight: '700' }}>€{cash.toFixed(2)}</td>
                <td style={{ color: '#6496FF',  fontWeight: '700' }}>€{(doc?.kgFee || 0).toFixed(2)}</td>
                <td style={{ color: '#4CAF7D',  fontWeight: '700' }}>€{(doc?.docFee || 0).toFixed(2)}</td>
                <td style={{ color: '#e8775a',  fontWeight: '700' }}>€{(online + cash).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── WITHDRAWALS SECTION ───────────────────────────────────────────────────────

function WithdrawalsSection() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('pending');

  useEffect(() => { fetchWithdrawals(); }, []);

  async function fetchWithdrawals() {
    setLoading(true);
    const { data } = await supabase
      .from('withdrawal_requests')
      .select('*, profiles(full_name, city)')
      .order('created_at', { ascending: false });
    setRequests(data || []);
    setLoading(false);
  }

  async function updateStatus(id, status) {
    await supabase.from('withdrawal_requests').update({ status, processed_at: new Date().toISOString() }).eq('id', id);
    setRequests(r => r.map(x => x.id === id ? { ...x, status } : x));
  }

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const totalPending = requests.filter(r => r.status === 'pending').reduce((s, r) => s + (r.amount || 0), 0);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><div className="spinner" /></div>;

  return (
    <>
      <div className="admin-stats">
        <StatCard label="Total requests"   value={requests.length} />
        <StatCard label="Pending"          value={requests.filter(r => r.status === 'pending').length} highlight={requests.filter(r => r.status === 'pending').length > 0} />
        <StatCard label="Pending amount"   value={`€${totalPending.toFixed(2)}`} sub="Awaiting processing" highlight={totalPending > 0} />
        <StatCard label="Processed"        value={requests.filter(r => r.status === 'processed').length} />
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h3>Withdrawal Requests</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 10px', color: 'var(--sand)', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
            >
              {['all','pending','processed','rejected'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <button className="admin-btn admin-btn-ghost" onClick={fetchWithdrawals}>↻ Refresh</button>
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Transporter</th>
              <th>Amount</th>
              <th>Bank country</th>
              <th>Account details</th>
              <th>Requested</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="admin-table-empty">No withdrawal requests</td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.id}>
                <td>
                  <div style={{ fontWeight: '600', color: 'var(--sand)' }}>{r.profiles?.full_name || '—'}</div>
                  <div style={{ fontSize: '11px', color: 'var(--olive)' }}>{r.profiles?.city}</div>
                </td>
                <td style={{ color: '#2ecc71', fontWeight: '700', fontSize: '15px' }}>€{parseFloat(r.amount || 0).toFixed(2)}</td>
                <td>{r.bank_country === 'DE' ? '🇩🇪 Germany' : '🇹🇳 Tunisia'}</td>
                <td style={{ fontSize: '12px', color: 'var(--olive)', fontFamily: 'monospace', maxWidth: '180px' }}>
                  <div>{r.account_holder}</div>
                  {r.iban && <div>IBAN: {r.iban}</div>}
                  {r.bic  && <div>BIC: {r.bic}</div>}
                  {r.bank_name && <div>{r.bank_name}</div>}
                  {r.account_number && <div>Acct: {r.account_number}</div>}
                </td>
                <td style={{ fontSize: '12px', color: 'var(--olive)' }}>{format(new Date(r.created_at), 'MMM d, yyyy HH:mm')}</td>
                <td>
                  {r.status === 'pending'   && <span className="badge badge-yellow">⏳ Pending</span>}
                  {r.status === 'processed' && <span className="badge badge-green">✓ Processed</span>}
                  {r.status === 'rejected'  && <span className="badge badge-gray">✗ Rejected</span>}
                </td>
                <td>
                  {r.status === 'pending' && (
                    <div className="admin-actions">
                      <button className="admin-btn admin-btn-verify"  onClick={() => updateStatus(r.id, 'processed')}>✓ Mark processed</button>
                      <button className="admin-btn admin-btn-danger"  onClick={() => updateStatus(r.id, 'rejected')}>✗ Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── REVIEWS SECTION ───────────────────────────────────────────────────────────

function ReviewsSection({ reviews, onDelete }) {
  const [search, setSearch]   = useState('');
  const [confirm, setConfirm] = useState(null);

  const filtered = reviews.filter(r =>
    (r.comment || '').toLowerCase().includes(search.toLowerCase()) ||
    r.reviewee?.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="admin-stats">
        <StatCard label="Total reviews"       value={reviews.length} />
        <StatCard label="Transporter reviews" value={reviews.filter(r => r.reviewer_role === 'sender').length} />
        <StatCard label="Sender reviews"      value={reviews.filter(r => r.reviewer_role === 'transporter').length} />
        <StatCard label="Avg rating"          value={reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) + ' ★' : '—'} />
      </div>

      {confirm && (
        <ConfirmDialog
          message="Delete this review permanently? This cannot be undone."
          onCancel={() => setConfirm(null)}
          onConfirm={() => { onDelete(confirm); setConfirm(null); }}
        />
      )}

      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h3>All Reviews</h3>
          <input className="admin-search" placeholder="Search by name or comment…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Reviewer role</th>
              <th>Reviewee</th>
              <th>Rating</th>
              <th>Comment</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="admin-table-empty">No reviews found</td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.id}>
                <td><RoleBadge role={r.reviewer_role} /></td>
                <td style={{ fontWeight: '600', color: 'var(--sand)' }}>{r.reviewee?.full_name || '—'}</td>
                <td>
                  <span style={{ color: '#F5A623' }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                  <span style={{ color: 'var(--olive)', fontSize: '11px', marginLeft: '4px' }}>{r.rating}/5</span>
                </td>
                <td style={{ maxWidth: '200px', color: 'rgba(245,237,216,0.6)', fontSize: '12px' }}>
                  {r.comment
                    ? `"${r.comment.slice(0, 80)}${r.comment.length > 80 ? '…' : ''}"`
                    : <span style={{ color: 'var(--olive)' }}>No comment</span>}
                </td>
                <td style={{ fontSize: '12px', color: 'var(--olive)' }}>{format(new Date(r.created_at), 'MMM d, yyyy')}</td>
                <td>
                  <button className="admin-btn admin-btn-danger" onClick={() => setConfirm(r.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── AGREEMENTS SECTION ────────────────────────────────────────────────────────

function AgreementsSection({ agreements }) {
  const [search, setSearch] = useState('');

  const filtered = agreements.filter(a =>
    a.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="admin-stats">
        <StatCard label="Total agreements" value={agreements.length} />
        <StatCard label="Transporters"     value={agreements.filter(a => a.role === 'transporter').length} />
        <StatCard label="Senders"          value={agreements.filter(a => a.role === 'sender').length} />
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <h3>Liability Agreements Log</h3>
          <input className="admin-search" placeholder="Search by name…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Reference</th>
              <th>Version</th>
              <th>Agreed at</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="admin-table-empty">No agreements recorded yet</td></tr>
            )}
            {filtered.map(a => (
              <tr key={a.id}>
                <td style={{ fontWeight: '600', color: 'var(--sand)' }}>{a.full_name}</td>
                <td><RoleBadge role={a.role} /></td>
                <td style={{ fontSize: '12px', color: 'var(--olive)', fontFamily: 'monospace' }}>
                  {a.trip_id ? `Trip: ${a.trip_id.slice(0, 8)}…` : a.booking_id ? `Booking: ${a.booking_id.slice(0, 8)}…` : '—'}
                </td>
                <td style={{ fontSize: '12px' }}>{a.agreement_version}</td>
                <td style={{ fontSize: '12px', color: 'var(--olive)' }}>{format(new Date(a.agreed_at), 'MMM d, yyyy HH:mm')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── ID VERIFICATION SECTION ───────────────────────────────────────────────────

function VerificationSection({ users, onApprove, onReject }) {
  const [pendingUsers, setPendingUsers] = React.useState([]);
  const [loading, setLoading]           = React.useState(true);
  const [viewingUser, setViewingUser]   = React.useState(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectingId, setRejectingId]   = React.useState(null);
  const [docUrls, setDocUrls]           = React.useState({});

  React.useEffect(() => { fetchPending(); }, []);

  async function fetchPending() {
    setLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('verification_status', 'pending')
      .order('created_at', { ascending: false });
    setPendingUsers(data || []);
    setLoading(false);
  }

  async function loadDocUrls(user) {
    const urls = {};
    if (user.id_document_path) {
      const { data } = await supabase.storage.from('verifications').createSignedUrl(user.id_document_path, 3600);
      if (data) urls.id = data.signedUrl;
    }
    if (user.selfie_path) {
      const { data } = await supabase.storage.from('verifications').createSignedUrl(user.selfie_path, 3600);
      if (data) urls.selfie = data.signedUrl;
    }
    setDocUrls(urls);
    setViewingUser(user);
  }

  async function handleApprove(userId) {
    await supabase.from('profiles').update({ is_verified: true, verification_status: 'approved' }).eq('id', userId);
    setPendingUsers(u => u.filter(x => x.id !== userId));
    setViewingUser(null);
    onApprove(userId);
  }

  async function handleReject(userId) {
    await supabase.from('profiles').update({
      verification_status: 'rejected',
      verification_reject_reason: rejectReason || 'Documents could not be verified. Please re-submit with clearer images.',
    }).eq('id', userId);
    setPendingUsers(u => u.filter(x => x.id !== userId));
    setViewingUser(null);
    setRejectingId(null);
    setRejectReason('');
    onReject(userId);
  }

  const allRejected = users.filter(u => u.verification_status === 'rejected');
  const allApproved = users.filter(u => u.is_verified);

  return (
    <div>
      <div className="admin-stats">
        <StatCard label="Pending review" value={pendingUsers.length} highlight={pendingUsers.length > 0} />
        <StatCard label="Approved"       value={allApproved.length} />
        <StatCard label="Rejected"       value={allRejected.length} />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><div className="spinner" /></div>
      ) : pendingUsers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--olive)' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
          <div style={{ fontWeight: '700', color: 'var(--sand)', marginBottom: '6px' }}>All clear</div>
          <div style={{ fontSize: '14px' }}>No pending verification requests.</div>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th><th>Role</th><th>Submitted</th><th>Documents</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingUsers.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: '600' }}>{u.full_name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--olive)' }}>{u.city}</div>
                  </td>
                  <td><RoleBadge role={u.role} /></td>
                  <td style={{ fontSize: '13px', color: 'var(--olive)' }}>
                    {u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '—'}
                  </td>
                  <td>
                    <button className="admin-btn admin-btn-ghost" onClick={() => loadDocUrls(u)}>🔍 Review docs</button>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-btn admin-btn-verify" onClick={() => handleApprove(u.id)}>✓ Approve</button>
                      <button className="admin-btn admin-btn-danger" onClick={() => { setRejectingId(u.id); setViewingUser(null); }}>✗ Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingUser && (
        <div className="admin-confirm-backdrop" onClick={() => setViewingUser(null)}>
          <div className="admin-confirm-box" style={{ maxWidth: '700px', width: '100%', padding: '28px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontFamily: 'Syne', fontWeight: '700' }}>Documents — {viewingUser.full_name}</h3>
              <button className="admin-btn admin-btn-ghost" onClick={() => setViewingUser(null)}>✕</button>
            </div>
            <div className="admin-stats" style={{ marginBottom: '24px' }}>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--olive)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>ID Document</div>
                {docUrls.id
                  ? <a href={docUrls.id} target="_blank" rel="noreferrer"><img src={docUrls.id} alt="ID" style={{ width: '100%', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', cursor: 'zoom-in' }} /></a>
                  : <div style={{ color: 'var(--olive)', fontSize: '13px' }}>No document uploaded</div>}
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--olive)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Selfie with ID</div>
                {docUrls.selfie
                  ? <a href={docUrls.selfie} target="_blank" rel="noreferrer"><img src={docUrls.selfie} alt="Selfie" style={{ width: '100%', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', cursor: 'zoom-in' }} /></a>
                  : <div style={{ color: 'var(--olive)', fontSize: '13px' }}>No selfie uploaded</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="admin-btn admin-btn-verify" style={{ flex: 1, padding: '12px' }} onClick={() => handleApprove(viewingUser.id)}>✓ Approve</button>
              <button className="admin-btn admin-btn-danger" style={{ flex: 1, padding: '12px' }} onClick={() => { setRejectingId(viewingUser.id); setViewingUser(null); }}>✗ Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectingId && (
        <div className="admin-confirm-backdrop" onClick={() => setRejectingId(null)}>
          <div className="admin-confirm-box" onClick={e => e.stopPropagation()}>
            <p style={{ fontWeight: '700', marginBottom: '12px' }}>Reason for rejection</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="e.g. ID is blurry, selfie doesn't match..."
              rows={3}
              style={{ width: '100%', marginBottom: '16px' }}
            />
            <div className="admin-confirm-actions">
              <button className="admin-btn admin-btn-ghost" onClick={() => setRejectingId(null)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={() => handleReject(rejectingId)}>Send Rejection</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── NAV ───────────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: 'overview',      icon: '📊', label: 'Overview' },
  { id: 'waitlist',      icon: '🚀', label: 'Waitlist' },
  { id: 'verification',  icon: '🛡️', label: 'Verification' },
  { id: 'users',         icon: '👥', label: 'Users' },
  { id: 'trips',         icon: '✈️', label: 'Trips' },
  { id: 'bookings',      icon: '📦', label: 'Bookings' },
  { id: 'revenue',       icon: '💶', label: 'Revenue' },
  { id: 'withdrawals',   icon: '💸', label: 'Withdrawals' },
  { id: 'reviews',       icon: '⭐', label: 'Reviews' },
  { id: 'agreements',    icon: '📋', label: 'Agreements' },
];

// ── WAITLIST SECTION ──────────────────────────────────────────────────────────

function WaitlistSection({ waitlist, onActivate, onDelete }) {
  const [search,     setSearch]     = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [sort,       setSort]       = useState('position');

  const total        = waitlist.length;
  const founding     = waitlist.filter(w => w.tier === 'founding').length;
  const earlyBird    = waitlist.filter(w => w.tier === 'early_bird').length;
  const activated    = waitlist.filter(w => w.activated).length;
  const senders      = waitlist.filter(w => w.role === 'sender').length;
  const transporters = waitlist.filter(w => w.role === 'transporter').length;
  const both         = waitlist.filter(w => w.role === 'both').length;
  const consented    = waitlist.filter(w => w.marketing_consent).length;

  // Channel breakdown
  const channels = waitlist.reduce((acc, w) => {
    const k = w.how_heard || 'unknown';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const topChannel = Object.entries(channels).sort((a, b) => b[1] - a[1])[0];

  // Country breakdown
  const countries = waitlist.reduce((acc, w) => {
    const k = w.country || 'unknown';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const filtered = waitlist
    .filter(w => {
      if (roleFilter !== 'all' && w.role !== roleFilter) return false;
      if (tierFilter !== 'all' && w.tier !== tierFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return w.full_name?.toLowerCase().includes(q) ||
               w.email?.toLowerCase().includes(q) ||
               w.country?.toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      if (sort === 'position') return a.position - b.position;
      if (sort === 'date')     return new Date(b.created_at) - new Date(a.created_at);
      if (sort === 'name')     return a.full_name?.localeCompare(b.full_name);
      return 0;
    });

  function exportCSV() {
    const headers = ['Position', 'Name', 'Email', 'Role', 'Country', 'Tier', 'Phone', 'How Heard', 'Feedback', 'Referral Code', 'Referred By', 'Marketing Consent', 'Activated', 'Signed Up'];
    const rows = waitlist.map(w => [
      w.position, w.full_name, w.email, w.role, w.country, w.tier,
      w.phone || '', w.how_heard || '', (w.feedback || '').replace(/,/g, ';'),
      w.referral_code, w.referred_by_code || '',
      w.marketing_consent ? 'Yes' : 'No',
      w.activated ? 'Yes' : 'No',
      new Date(w.created_at).toLocaleDateString('en-GB'),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `transportily-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const TIER_STYLES = {
    founding:   { bg: 'rgba(232,52,26,0.12)',  color: '#E8341A',  label: '🏅 Founding' },
    early_bird: { bg: 'rgba(232,197,71,0.12)', color: '#E8C547',  label: '🐦 Early Bird' },
    waitlist:   { bg: 'rgba(76,175,125,0.1)',  color: '#4CAF7D',  label: '✅ Waitlist' },
  };

  const ROLE_STYLES = {
    sender:      { bg: 'rgba(232,52,26,0.1)',   color: '#E8341A',  label: '📦 Sender' },
    transporter: { bg: 'rgba(76,175,125,0.1)',  color: '#4CAF7D',  label: '✈️ Transporter' },
    both:        { bg: 'rgba(232,197,71,0.1)',  color: '#E8C547',  label: '🔄 Both' },
  };

  return (
    <div>
      {/* ── Stats ── */}
      <div className="admin-stats" style={{ marginBottom: 24 }}>
        <StatCard label="Total signups"      value={total} highlight />
        <StatCard label="🏅 Founding"        value={founding}  sub={`${100 - founding} spots left`} highlight={founding > 0} />
        <StatCard label="🐦 Early Bird"      value={earlyBird} sub={`${400 - earlyBird} spots left`} />
        <StatCard label="✅ Activated"        value={activated} sub="Converted to accounts" />
        <StatCard label="📦 Senders"          value={senders} />
        <StatCard label="✈️ Transporters"    value={transporters} />
        <StatCard label="🔄 Both"             value={both} />
        <StatCard label="📧 Marketing OK"    value={consented} sub={`${Math.round((consented/total)*100)||0}% consent rate`} />
        <StatCard label="📣 Top channel"     value={topChannel?.[0] || '—'} sub={topChannel ? `${topChannel[1]} signups` : ''} />
      </div>

      {/* ── Country breakdown ── */}
      <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: 'var(--sand)' }}>🌍 Signups by country</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {Object.entries(countries).sort((a,b) => b[1]-a[1]).map(([country, count]) => (
            <div key={country} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px', fontSize: 13 }}>
              <strong style={{ color: 'var(--sand)' }}>{country}</strong>
              <span style={{ color: 'var(--olive)', marginLeft: 6 }}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Feedback preview ── */}
      {waitlist.some(w => w.feedback) && (
        <div style={{ background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: 'var(--sand)' }}>💬 Recent feedback</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {waitlist.filter(w => w.feedback).slice(0, 5).map(w => (
              <div key={w.id} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 14px', fontSize: 13 }}>
                <span style={{ color: 'var(--olive)', marginRight: 8 }}>#{w.position} {w.full_name}:</span>
                <span style={{ color: 'var(--sand)' }}>{w.feedback}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="admin-table-wrap">
        <div className="admin-table-header">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="admin-search"
              placeholder="Search name, email, country…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="admin-filter-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
              <option value="all">All roles</option>
              <option value="sender">Senders</option>
              <option value="transporter">Transporters</option>
              <option value="both">Both</option>
            </select>
            <select className="admin-filter-select" value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
              <option value="all">All tiers</option>
              <option value="founding">Founding</option>
              <option value="early_bird">Early Bird</option>
              <option value="waitlist">Waitlist</option>
            </select>
            <select className="admin-filter-select" value={sort} onChange={e => setSort(e.target.value)}>
              <option value="position">Sort: Position</option>
              <option value="date">Sort: Newest</option>
              <option value="name">Sort: Name</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--olive)' }}>{filtered.length} entries</span>
            <button className="admin-btn admin-btn-ghost" onClick={exportCSV}>⬇ Export CSV</button>
          </div>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Country</th>
              <th>Tier</th>
              <th>Referrals</th>
              <th>Channel</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="admin-table-empty">No entries found</td></tr>
            )}
            {filtered.map(w => {
              const tier = TIER_STYLES[w.tier] || TIER_STYLES.waitlist;
              const role = ROLE_STYLES[w.role]  || { bg: 'transparent', color: 'var(--olive)', label: w.role };
              const referralCount = waitlist.filter(x => x.referred_by_code === w.referral_code).length;
              return (
                <tr key={w.id}>
                  <td style={{ fontWeight: 900, color: 'var(--sand)', fontFamily: 'Syne' }}>#{w.position}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--sand)' }}>{w.full_name}</div>
                    {w.phone && <div style={{ fontSize: 11, color: 'var(--olive)' }}>{w.phone}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <a href={`mailto:${w.email}`} style={{ color: 'var(--olive)' }}>{w.email}</a>
                  </td>
                  <td>
                    <span style={{ background: role.bg, color: role.color, borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                      {role.label}
                    </span>
                  </td>
                  <td style={{ fontSize: 13 }}>{w.country}</td>
                  <td>
                    <span style={{ background: tier.bg, color: tier.color, borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                      {tier.label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {referralCount > 0
                      ? <span style={{ background: 'rgba(76,175,125,0.1)', color: '#4CAF7D', borderRadius: 100, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{referralCount}</span>
                      : <span style={{ color: 'var(--olive)', fontSize: 12 }}>—</span>
                    }
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--olive)' }}>{w.how_heard || '—'}</td>
                  <td style={{ fontSize: 11, color: 'var(--olive)', whiteSpace: 'nowrap' }}>
                    {new Date(w.created_at).toLocaleDateString('en-GB')}
                  </td>
                  <td>
                    {w.activated
                      ? <span style={{ color: '#4CAF7D', fontSize: 12, fontWeight: 700 }}>✓ Activated</span>
                      : <span style={{ color: 'var(--olive)', fontSize: 12 }}>Pending</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!w.activated && (
                        <button
                          className="admin-btn admin-btn-sm"
                          onClick={() => onActivate(w.id)}
                          title="Mark as activated"
                        >✓ Activate</button>
                      )}
                      <button
                        className="admin-btn admin-btn-sm admin-btn-danger"
                        onClick={() => onDelete(w.id)}
                        title="Delete entry"
                      >✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── MAIN ADMIN PAGE ───────────────────────────────────────────────────────────

export default function Admin() {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [section, setSection]       = useState('overview');
  const [dataLoading, setDataLoading] = useState(false);

  const [users,      setUsers]      = useState([]);
  const [trips,      setTrips]      = useState([]);
  const [bookings,   setBookings]   = useState([]);
  const [reviews,    setReviews]    = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [waitlist,   setWaitlist]   = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (profile?.is_admin) fetchAll();
    else if (profile && !profile.is_admin) navigate('/');
  }, [profile, authLoading]);

  async function fetchAll() {
    setDataLoading(true);
    const [uRes, tRes, bRes, rRes, aRes, wRes] = await Promise.all([
      supabase.from('profiles').select('*').order('created_at', { ascending: false }),
      supabase.from('trips').select('*, transporter:profiles!transporter_id(full_name), payment_mode, is_multi_stop, accepts_documents, document_price').order('departure_date', { ascending: false }),
      supabase.from('bookings').select('*, sender:profiles!sender_id(full_name), trips(origin_city, destination_city, transporter_id, profiles!transporter_id(full_name)), payment_status, sender_payment_mode, booking_type, document_type, document_count').order('created_at', { ascending: false }),
      supabase.from('reviews').select('*, reviewee:profiles!reviewee_id(full_name)').order('created_at', { ascending: false }),
      supabase.from('liability_agreements').select('*').order('agreed_at', { ascending: false }),
      supabase.from('waitlist').select('*').order('position', { ascending: true }),
    ]);
    setUsers(uRes.data || []);
    setTrips(tRes.data || []);
    setBookings(bRes.data || []);
    setReviews(rRes.data || []);
    setAgreements(aRes.data || []);
    setWaitlist(wRes.data || []);
    setDataLoading(false);
  }

  async function verifyUser(userId) {
    await supabase.from('profiles').update({ is_verified: true }).eq('id', userId);
    setUsers(u => u.map(x => x.id === userId ? { ...x, is_verified: true } : x));
  }

  async function unverifyUser(userId) {
    await supabase.from('profiles').update({ is_verified: false }).eq('id', userId);
    setUsers(u => u.map(x => x.id === userId ? { ...x, is_verified: false } : x));
  }

  async function cancelTrip(tripId) {
    await supabase.from('trips').update({ status: 'cancelled' }).eq('id', tripId);
    await supabase.from('bookings').update({ status: 'cancelled' }).eq('trip_id', tripId).in('status', ['pending', 'proposed', 'confirmed']);
    setTrips(t => t.map(x => x.id === tripId ? { ...x, status: 'cancelled' } : x));
  }

  async function deleteTrip(tripId) {
    await supabase.from('trips').delete().eq('id', tripId);
    setTrips(t => t.filter(x => x.id !== tripId));
  }

  async function activateWaitlistEntry(id) {
    await supabase.from('waitlist').update({ activated: true }).eq('id', id);
    setWaitlist(w => w.map(x => x.id === id ? { ...x, activated: true } : x));
  }

  async function deleteWaitlistEntry(id) {
    await supabase.from('waitlist').delete().eq('id', id);
    setWaitlist(w => w.filter(x => x.id !== id));
  }

  async function deleteReview(reviewId) {
    await supabase.from('reviews').delete().eq('id', reviewId);
    setReviews(r => r.filter(x => x.id !== reviewId));
  }

  if (authLoading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) { navigate('/login'); return null; }
  if (!profile?.is_admin) {
    return (
      <div className="admin-access-denied">
        <div style={{ fontSize: '48px' }}>🔒</div>
        <h2>Access Denied</h2>
        <p>You don't have permission to view this page.</p>
        <Link to="/" className="btn btn-primary" style={{ marginTop: '16px' }}>Go Home</Link>
      </div>
    );
  }

  const totalRevenue     = bookings.filter(b => ['paid','cash'].includes(b.payment_status)).reduce((s, b) => s + (b.platform_fee || 0), 0);
  const pendingBookings  = bookings.filter(b => ['pending','proposed'].includes(b.status)).length;
  const pendingKYC       = users.filter(u => u.verification_status === 'pending').length;

  const sectionTitles = {
    overview:     { title: 'Overview',              desc: 'Platform summary at a glance' },
    waitlist:     { title: 'Waitlist',              desc: `${waitlist.length} people signed up` },
    verification: { title: 'ID Verification',       desc: 'Review and approve transporter ID documents' },
    users:        { title: 'Users',                 desc: 'Manage transporters and senders' },
    trips:        { title: 'Trips',                 desc: 'All posted trips on the platform' },
    bookings:     { title: 'Bookings',              desc: 'All booking requests and their status' },
    revenue:      { title: 'Revenue',               desc: 'Platform fee earnings and GMV' },
    withdrawals:  { title: 'Withdrawal Requests',   desc: 'Transporter payout requests' },
    reviews:      { title: 'Reviews',               desc: 'Moderate user reviews' },
    agreements:   { title: 'Liability Agreements',  desc: 'Log of all signed agreements' },
  };

  return (
    <div className="admin-page">

      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <h2>Transportily</h2>
          <span>Admin Dashboard</span>
        </div>

        <nav className="admin-nav">
          <div className="admin-nav-section">Navigation</div>
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              className={`admin-nav-item${section === item.id ? ' active' : ''}`}
              onClick={() => setSection(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
              {item.id === 'bookings'     && pendingBookings > 0 && <span className="nav-badge">{pendingBookings}</span>}
              {item.id === 'verification' && pendingKYC > 0      && <span className="nav-badge">{pendingKYC}</span>}
              {item.id === 'waitlist'     && waitlist.length > 0 && <span className="nav-badge">{waitlist.length}</span>}
            </button>
          ))}

          <div className="admin-nav-section" style={{ marginTop: '16px' }}>App</div>
          <Link to="/" className="admin-nav-item" style={{ textDecoration: 'none' }}>
            <span className="nav-icon">🏠</span>
            <span>Back to app</span>
          </Link>
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-nav-item" style={{ cursor: 'default', opacity: 0.6 }}>
            <span className="nav-icon">👤</span>
            <span style={{ fontSize: '12px' }}>{profile.full_name?.split(' ')[0]}</span>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="admin-main">
        <div className="admin-topbar">
          <div>
            <h1>{sectionTitles[section]?.title}</h1>
            <p>{sectionTitles[section]?.desc}</p>
          </div>
          <button className="admin-btn admin-btn-ghost" onClick={fetchAll}>↻ Refresh</button>
        </div>

        {dataLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            {section === 'overview' && (
              <>
                <div className="admin-stats">
                  <StatCard label="🚀 Waitlist signups"   value={waitlist.length} highlight={waitlist.length > 0} sub={`${waitlist.filter(w=>w.tier==='founding').length} founding · ${waitlist.filter(w=>w.tier==='early_bird').length} early bird`} />
                  <StatCard label="Total users"           value={users.length} />
                  <StatCard label="Active trips"          value={trips.filter(t => t.status === 'active').length} />
                  <StatCard label="Multi-stop trips"      value={trips.filter(t => t.is_multi_stop).length} />
                  <StatCard label="Trips w/ docs"         value={trips.filter(t => t.accepts_documents).length} sub="Accept document bookings" />
                  <StatCard label="Total bookings"        value={bookings.length} />
                  <StatCard label="Pending bookings"      value={pendingBookings} highlight={pendingBookings > 0} />
                  <StatCard label="📦 Kg bookings"        value={bookings.filter(b => !b.booking_type || b.booking_type === 'kg').length} />
                  <StatCard label="📄 Doc bookings"       value={bookings.filter(b => b.booking_type === 'document').length} />
                  <StatCard label="Platform revenue"      value={`€${totalRevenue.toFixed(2)}`} sub="Online + cash fees" highlight />
                  <StatCard label="Verified transporters" value={users.filter(u => u.is_verified).length} />
                  <StatCard label="Pending KYC"           value={pendingKYC} highlight={pendingKYC > 0} />
                  <StatCard label="Reviews"               value={reviews.length} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '8px' }}>
                  {NAV_ITEMS.filter(n => n.id !== 'overview').map(item => (
                    <button
                      key={item.id}
                      onClick={() => setSection(item.id)}
                      style={{ background: '#1a1a18', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '20px', cursor: 'pointer', textAlign: 'left', color: 'var(--sand)', fontFamily: 'inherit', transition: 'border-color 0.15s', position: 'relative' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
                    >
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{item.icon}</div>
                      <div style={{ fontWeight: '700', fontSize: '14px' }}>{item.label}</div>
                      <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>→ Manage</div>
                      {item.id === 'bookings'     && pendingBookings > 0 && <span className="nav-badge" style={{ position: 'absolute', top: '12px', right: '12px' }}>{pendingBookings}</span>}
                      {item.id === 'verification' && pendingKYC > 0      && <span className="nav-badge" style={{ position: 'absolute', top: '12px', right: '12px' }}>{pendingKYC}</span>}
                    </button>
                  ))}
                </div>
              </>
            )}

            {section === 'waitlist'     && <WaitlistSection waitlist={waitlist} onActivate={activateWaitlistEntry} onDelete={deleteWaitlistEntry} />}
            {section === 'verification' && (
              <VerificationSection
                users={users}
                onApprove={id => setUsers(u => u.map(x => x.id === id ? { ...x, is_verified: true, verification_status: 'approved' } : x))}
                onReject={id  => setUsers(u => u.map(x => x.id === id ? { ...x, verification_status: 'rejected' } : x))}
              />
            )}
            {section === 'users'       && <UsersSection users={users} onVerify={verifyUser} onUnverify={unverifyUser} />}
            {section === 'trips'       && <TripsSection trips={trips} onCancel={cancelTrip} onDelete={deleteTrip} />}
            {section === 'bookings'    && <BookingsSection bookings={bookings} />}
            {section === 'revenue'     && <RevenueSection bookings={bookings} />}
            {section === 'withdrawals' && <WithdrawalsSection />}
            {section === 'reviews'     && <ReviewsSection reviews={reviews} onDelete={deleteReview} />}
            {section === 'agreements'  && <AgreementsSection agreements={agreements} />}
          </>
        )}
      </main>
    </div>
  );
}